// src/features/services/ServicesPage.tsx
import { useEffect, useState } from 'react';
import { Archive, CheckCircle2, CircleOff, Server } from 'lucide-react';
import { EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { ControlPlaneDataSurface, ControlPlaneMetricStrip, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { ServiceCreateModal, ServiceTable } from './components';
import { useServices } from './hooks/useServices';
import './styles/services.css';

const PAGE_SIZE_OPTIONS = [20, 25, 50, 100] as const;

/**
 * Services Management Foundation. Same layout pattern as
 * CustomersPage: a PageContainer shell, a feature-owned header, then
 * feature content below — RTL and Persian throughout, using only
 * existing tokens/primitives (Card, PageHeader) via
 * app/components/foundation.
 *
 * Rows are loaded from the real Super Admin Services API through the
 * feature hook. Mock data is not used by this live page.
 */
export function ServicesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const {
    services,
    loading,
    error,
    creating,
    createError,
    create,
  } = useServices();

  const totalPages = Math.max(1, Math.ceil(services.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const visibleServices = services.slice((safePage - 1) * pageSize, safePage * pageSize);
  const activeCount = services.filter((service) => service.status === 'active').length;
  const inactiveCount = services.filter((service) => service.status === 'inactive').length;
  const archivedCount = services.filter((service) => service.status === 'archived').length;

  useEffect(() => {
    if (currentPage !== safePage) setCurrentPage(safePage);
  }, [currentPage, safePage]);

  async function handleCreate(input: Parameters<typeof create>[0]) {
    const created = await create(input);
    if (created) {
      setCreateOpen(false);
      setFeedback('سرویس با موفقیت ایجاد شد');
    }
  }

  return (
    <ControlPlanePage
      className="vania-services-control-plane-page"
      title="مدیریت سرویس‌ها"
      description="مدیریت سرویس‌های پلتفرم با مالکیت مشخص تننت"
      summary={services.length > 0 ? (
        <ControlPlaneMetricStrip
          items={[
            { label: 'کل سرویس‌ها', value: formatPersianNumber(services.length), icon: <Server size={16} />, tone: 'primary' },
            { label: 'فعال', value: formatPersianNumber(activeCount), icon: <CheckCircle2 size={16} />, tone: 'success' },
            { label: 'غیرفعال', value: formatPersianNumber(inactiveCount), icon: <CircleOff size={16} />, tone: 'danger' },
            { label: 'بایگانی‌شده', value: formatPersianNumber(archivedCount), icon: <Archive size={16} />, tone: 'warning' },
          ]}
        />
      ) : undefined}
    >
      <ControlPlaneSection>
        <ControlPlaneDataSurface>
          {feedback && <p className="vania-service-action-success">{feedback}</p>}
          {error && services.length === 0 ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
          <div className="vania-services-toolbar">
            <button type="button" className="vania-services-action-button" onClick={() => { setFeedback(null); setCreateOpen(true); }}>ایجاد سرویس</button>
          </div>
          <div className="vania-services-table-region">
            {error && services.length === 0 ? null : loading && services.length === 0 ? <ServicesTableSkeleton /> : !loading && services.length === 0 ? <EmptyState message="سرویسی یافت نشد." /> : <ServiceTable services={visibleServices} />}
          </div>
          <div className="vania-services-pagination-bar">
            <label className="vania-services-page-size"><span>تعداد در صفحه</span><select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setCurrentPage(1); }} aria-label="تعداد سرویس در هر صفحه">{PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
            <nav className="vania-services-pagination" aria-label="صفحه‌بندی سرویس‌ها">
              <button type="button" className="vania-services-pagination__control" disabled={safePage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>‹</button>
              <div className="vania-services-pagination__pages">{Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => <button type="button" key={page} className={`vania-services-pagination__page${page === safePage ? ' is-active' : ''}`} aria-current={page === safePage ? 'page' : undefined} onClick={() => setCurrentPage(page)}>{formatPersianNumber(page)}</button>)}</div>
              <button type="button" className="vania-services-pagination__control" disabled={safePage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>›</button>
            </nav>
          </div>
        </ControlPlaneDataSurface>
      </ControlPlaneSection>
      <ServiceCreateModal
        open={createOpen}
        submitting={creating}
        error={createError}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
    </ControlPlanePage>
  );
}

function ServicesTableSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری سرویس‌ها">
      <div className="vania-services-table-scroll" aria-hidden="true">
          <table className="vania-services-table">
            <thead>
              <tr>
                {['نام سرویس', 'تننت', 'شناسه', 'نوع', 'وضعیت', 'تاریخ ایجاد'].map((heading) => <th key={heading}>{heading}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 20 }, (_, rowIndex) => (
                <tr key={`service-skeleton-${rowIndex}`}>
                  {Array.from({ length: 6 }, (_, cellIndex) => (
                    <td key={`service-skeleton-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '72%' : '55%' }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    </VaniaLoadingSystem>
  );
}
